import { Component } from 'react'

const RELOAD_GUARD_KEY = 'cjm_chunk_reload'

// Vite/Rolldown builds hash every JS chunk by content, and this site's own
// deploy caches assets/** as immutable for a year (see public/serve.json) —
// correct for content that hasn't changed, but it means once a new deploy
// replaces dist/, the OLD chunk files are simply gone from the server. A
// visitor who already had the site open (tab left open across a deploy, or
// restored from bfcache) still has JS that calls import() for those old,
// now-404ing chunk URLs the moment they click a lazy-loaded nav link (e.g.
// "Websites"). React.lazy() throws when that import rejects, and with no
// error boundary around Suspense, React just unmounts the whole tree —
// since the page background is black (see index.css), that reads as the
// page "going black" for no visible reason. A one-time hard reload fetches
// the current index.html + current chunk hashes and fixes it transparently.
function isChunkLoadError(error) {
  const msg = String(error?.message || error || '')
  return /dynamically imported module|Failed to fetch|Loading chunk|ChunkLoadError|MIME type/i.test(msg)
}

export default class RouteErrorBoundary extends Component {
  state = { hasError: false }

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  componentDidCatch(error) {
    if (isChunkLoadError(error) && !sessionStorage.getItem(RELOAD_GUARD_KEY)) {
      // Guard against a reload loop if the error isn't actually stale-chunk
      // related (e.g. the server is genuinely down) — reload only once per
      // session, then fall back to the manual-retry UI below.
      sessionStorage.setItem(RELOAD_GUARD_KEY, '1')
      window.location.reload()
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 99999,
          background: '#000', color: '#fff',
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', gap: 20,
          textAlign: 'center', padding: 24,
          fontFamily: 'Geist Variable, sans-serif',
        }}>
          <p style={{ fontSize: 16, color: 'rgba(255,255,255,0.7)' }}>
            Something went wrong loading this page.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="btn-primary"
            style={{ border: 'none', cursor: 'pointer' }}
          >
            Reload
          </button>
        </div>
      )
    }
    return this.props.children
  }
}
